// The project drawer of the Budgets tab (SPEC §8.6): a project, or one row of the Projects list, in
// a period, with its project spend and spend on unit budgets, and every PO behind it.
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { formatDate, sameProject, type ProjectRef } from "@po/shared";
import { api, qs, type ProjectLine } from "../lib/api";
import { useDashboard } from "../pages/Dashboard";
import { StageBadge } from "./badges";
import { usd } from "./budget";
import { Stepper } from "./BudgetLineDrawer";
import type { DrawerRef, ProjectStep } from "./Drawer";

const periodLabel = (p: { year: number; quarter: number | null }) => (p.quarter === null ? `Year ${p.year}` : `Q${p.quarter} ${p.year}`);
const left = { textAlign: "left" } as const;
const spendOf = (f: { committedUsd: number; accruedUsd: number }) => f.committedUsd + f.accruedUsd;

/** The project, and the coding and where it was charged when the drawer shows one row of it. */
function title(ref: ProjectRef, name: string | null) {
  const project = ref.projectNo ? `${ref.projectNo}${name ? ` · ${name}` : ""}` : "No project";
  const row =
    ref.purchaseTypeNo !== undefined || ref.technicalAccountNo !== undefined
      ? [ref.purchaseTypeNo ?? "No purchase type", ref.technicalAccountNo ?? "No technical account"].join(" · ")
      : null;
  return { project, row };
}

export function ProjectBody({
  r,
  period,
  onOpen,
  steps,
  onStep,
}: {
  r: ProjectRef;
  period: string;
  onOpen: (r: DrawerRef) => void;
  steps?: ProjectStep[];
  onStep?: (s: ProjectStep) => void;
}) {
  const { filters, search } = useDashboard();
  const params = { vessel: filters.vessel, period, forecasts: search.forecasts, ref: JSON.stringify(r) };
  const { data: p, isLoading, isError, isPlaceholderData } = useQuery({
    queryKey: ["budgets/project", params],
    queryFn: () => api<ProjectLine>(`/budgets/project${qs(params)}`),
    // Stepping keeps the last project on screen, dimmed, until the next one loads.
    placeholderData: (prev) => prev,
  });
  const stepper = steps && onStep && <Stepper steps={steps} at={steps.findIndex((st) => st.period === period && sameProject(st.ref, r))} onStep={onStep} />;
  if (isLoading)
    return (
      <>
        {stepper}
        <div className="p-4 text-muted">Loading…</div>
      </>
    );
  if (isError || !p)
    return (
      <>
        {stepper}
        <div className="p-4 text-muted">This project has no spend in this period.</div>
      </>
    );

  const { project, row } = title(r, p.projectName);
  const name = (u: string) => p.unitNames[u] ?? u;
  const total = p.projectSpendUsd + p.onUnitBudgetsUsd;
  // Other cost applies only in a period holding the current quarter (null elsewhere): "—" then.
  const withOther = p.otherCostUsd !== null;
  return (
    <div className={isPlaceholderData ? "opacity-60" : undefined}>
      {stepper}
      <div className="px-4 pt-3 pb-2">
        <div className="label">Project · {periodLabel(p.period)}</div>
        <h2 className="text-[18px] font-extrabold leading-snug">
          {project}
          {row && <span className="text-muted font-semibold"> · {row}</span>}
        </h2>
        {r.projectSpend !== undefined && (
          <p className="text-[12px] text-muted">{r.projectSpend ? "Project spend: a project-funded purchase type, charged to no vessel budget." : "On a vessel's budget line: also counted there."}</p>
        )}
      </div>
      <section className="card mx-4 my-3 p-3">
        <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
          <Term label="Project spend">{usd(p.projectSpendUsd)}</Term>
          <Op>+</Op>
          <Term label="On unit budgets">{usd(p.onUnitBudgetsUsd)}</Term>
          <Op>=</Op>
          <Term label="Actual" result>
            {usd(total)}
          </Term>
          <span className="flex-1" />
          {/* POs under approval, only in a period holding the current quarter, as on the sheets. */}
          <Term label="Other cost">{p.otherCostUsd === null ? "—" : usd(p.otherCostUsd)}</Term>
          <Term label="POs">{p.pos.length}</Term>
        </div>
      </section>
      <section className="py-3 border-t border-line">
        <div className="overflow-x-auto">
          <table className="sheet text-[12px]">
            <thead>
              <tr>
                <th>Date</th>
                <th style={left}>PO</th>
                <th style={left}>Vessel</th>
                {!row && <th style={left}>Purchase type · technical account</th>}
                <th style={left}>Supplier</th>
                <th style={left}>Stage</th>
                <th style={left} title="Sertica user no: who ordered the PO, else created it">User</th>
                <th title="Committed + accrued">Actual</th>
                <th title="Under approval: its value, to be spent once ordered. Only in a period holding the current quarter">Other cost</th>
              </tr>
            </thead>
            <tbody>
              {p.pos.map((x) => (
                <tr key={x.poNo}>
                  <td style={{ whiteSpace: "nowrap" }}>{x.orderDate ? formatDate(x.orderDate) : "—"}</td>
                  <td style={left}>
                    <button className="text-brand font-semibold hover:underline" onClick={() => onOpen({ type: "po", no: x.poNo })}>
                      {x.poNo}
                    </button>
                  </td>
                  <td style={left}>{x.unitNos.map(name).join(", ")}</td>
                  {!row && <td style={{ ...left, whiteSpace: "normal" }}>{x.codings.join(", ")}</td>}
                  <td style={{ ...left, whiteSpace: "normal" }}>{x.supplier ?? "—"}</td>
                  <td style={left}>{x.stage ? <StageBadge stage={x.stage} lens="po" plain /> : "—"}</td>
                  <td style={left}>{x.userNo ?? "—"}</td>
                  <td className="font-semibold">{x.otherCostUsd && !spendOf(x) ? "—" : usd(spendOf(x))}</td>
                  <td>{x.otherCostUsd ? usd(x.otherCostUsd) : "—"}</td>
                </tr>
              ))}
              {p.pos.length > 0 ? (
                <tr className="total">
                  <td colSpan={row ? 6 : 7}>Total</td>
                  <td>{usd(p.pos.reduce((n, x) => n + spendOf(x), 0))}</td>
                  <td>{withOther ? usd(p.otherCostUsd ?? 0) : "—"}</td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={row ? 8 : 9} className="text-muted">
                    No POs in this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const Term = ({ label, children, result = false }: { label: string; children: ReactNode; result?: boolean }) => (
  <div className="min-w-0">
    <div className={`text-[11px] ${result ? "font-semibold text-ink2" : "text-muted"}`}>{label}</div>
    <div className={`tnum whitespace-nowrap ${result ? "text-[15px] font-extrabold" : "text-[13.5px] font-semibold"}`}>{children}</div>
  </div>
);

const Op = ({ children }: { children: string }) => (
  <span aria-hidden className="self-end pb-0.5 text-[15px] font-bold text-muted">
    {children}
  </span>
);
